import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    ElevatedCard,
    FullBleed,
    Heading1,
    NavLink,
    Stack,
} from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    GetBasicPrescriberOrdersDocument,
    PrescriberOrderStatus,
} from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import {
    type BasicPrescriberOrderFragment,
    sortOrdersByDate,
} from '../../../util';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { ShoppingHeader } from '../../common/shopping-header/ShoppingHeader';

import { MedicineCabinetCardHeader } from './MedicineCabinetCardHeader';
import { MedicineCabinetPlaceholder } from './MedicineCabinetPlaceholder';
import { UpToDateCard } from './orders-card/components/UpToDateCard';
import { MedicationOrders } from './orders-card/MedicationOrders';

export const MedicineCabinetPage = () => {
    const { t } = useTranslation();
    const { patient } = usePatientContext();
    const navigate = useNavigate();

    const input = {
        statusFilter: [PrescriberOrderStatus.New, PrescriberOrderStatus.OnHold],
    };

    const { data, loading } = useQuery(GetBasicPrescriberOrdersDocument, {
        variables: { patientId: patient?.id ?? '', input },
        errorPolicy: 'all',
    });

    if (!loading && !data) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    const medications: BasicPrescriberOrderFragment[] = [];

    data?.patient?.prescriberOrdersConnection.edges.forEach((edge) => {
        medications.push(getFragment(getFragment(edge.node)));
    });

    data?.patient?.unclaimedPrescriberOrdersConnection.edges.forEach((edge) => {
        medications.push(getFragment(edge.node));
    });

    data?.patient?.consentedPatientsConnection.edges.forEach((patientEdge) => {
        patientEdge.node.prescriberOrdersConnection.edges.forEach((edge) => {
            medications.push(getFragment(getFragment(edge.node)));
        });
    });

    const orders = sortOrdersByDate(medications);

    return (
        <Stack gap="lg">
            <ShoppingHeader>
                <Heading1>{t('cabinet.heading')}</Heading1>
            </ShoppingHeader>
            <FullBleed breakpoint="md" color="primary">
                {loading ? (
                    <MedicineCabinetPlaceholder variant="elevated" />
                ) : orders.length ? (
                    <ElevatedCard
                        header={
                            <MedicineCabinetCardHeader
                                showButton={false}
                                count={orders.length}
                            />
                        }
                    >
                        <MedicationOrders orders={orders} showAll />
                    </ElevatedCard>
                ) : (
                    <UpToDateCard />
                )}
            </FullBleed>
            <NavLink
                label={t('cabinet.prescriptionHistory')}
                onClick={() => navigate(getAbsoluteRoute('cabinet.history'))}
                variant="subtle"
            />
        </Stack>
    );
};
